import { internalItemPieceCount } from "./compartments";
import { shelvingLightingQuantities, type LightingQuantities } from "./lighting";
import { cabinetLightingQuantities } from "./cabinetLighting";
import { backPanelDecor } from "./backPanel";
import type { FacadeType, PlannerProject, ShelvingCellConfig, WardrobeModule } from "../types";
import { cornerFillerAreaM2, cornerLegPoints } from "./corner";
import { cabinetBaseHeightMm, cabinetBaseType } from "./installation";
import { hingeCountForDoorHeight, mezzanineFlapLayout } from "./moduleRules";
import { cellFrontLeaves, flapFront, flapHingeCount, type FlapFront, shelvingExcludedLifts } from "./flapFronts";
import { moduleHeadroomMm, shelvingCellHeadroomMm } from "./flapHeadroom";
import { CABINET_DRAWERS_FINGER_PULL, cabinetDrawerFinish, cabinetDrawerFrontsAreaM2, cabinetDrawerLayout } from "./cabinetDrawers";
import { handleExtraLengthMm, type HandleGeometry } from "./handles";
import {
  getShelvingCellBoards,
  getShelvingCellLayouts,
  getShelvingGridLayout,
  shelvingCellFacadeRect,
  shelvingDrawerCount,
  drawerLimits,
  shelvingDrawerFrontHeights,
  shelvingLegCount
} from "./shelving";
import { builtPanelAreaM2, builtPanelsAreaM2, cabinetDoorLeaves, cabinetFacadeHeightMm, moduleBuiltPanels } from "./builtParts";
import { facadePaintedOnBothSides, facadeSupportsMilling } from "./facades";
import { activeSideMirrors, sideMirrorSize } from "./sideMirrors";
import { resolveDecor } from "./decors";
import { doorFacadeOverride, leafFinish, leafHandle } from "./doorFacades";
import { drawerFrontFinish, interiorDrawerFrontsAreaM2, interiorShelfAreaM2, normalizeShelfDecor } from "./interiorDecor";
import { chestCellSplit, chestDrawerPaintFinish, CHEST_TOP_FLAP_EXCLUDED_LIFTS } from "./chest";
import { normalizeHandleChoice } from "../catalog";
import { createPriceReader, priceKeys, priceLineTitle, type PriceReader, type PriceValues } from "./priceList";

export interface PriceBreakdown {
  carcass: number;
  internals: number;
  facades: number;
  hardware: number;
  total: number;
  /** Строки прайса, цена которых не задана: итог неокончательный. */
  missing: string[];
}

const roundHundreds = (value: number) => Math.round(value / 100) * 100;

/** Группа декора Egger, по которой берётся цена плиты. */
const carcassGroup = (project: PlannerProject, module: WardrobeModule) =>
  resolveDecor(module.decor, module.material ?? project.carcassMaterial).group;

const facadeGroup = (
  project: PlannerProject,
  module: WardrobeModule,
  finish: { facadeDecor?: string; facadeMaterial?: WardrobeModule["facadeMaterial"] }
) =>
  finish.facadeDecor !== undefined || finish.facadeMaterial !== undefined
    ? resolveDecor(finish.facadeDecor, finish.facadeMaterial).group
    : carcassGroup(project, module);

type FacadeFinish = {
  facade?: FacadeType;
  facadePattern?: WardrobeModule["facadePattern"];
  facadeDecor?: string;
  facadeMaterial?: WardrobeModule["facadeMaterial"];
  facadePaintSides?: WardrobeModule["facadePaintSides"];
};

/** Цена фасада за м²: ЛДСП — по плите своего декора, остальное — по прайсу фасадов. */
const facadeRatePerM2 = (
  prices: PriceReader,
  project: PlannerProject,
  module: WardrobeModule,
  finish: FacadeFinish
) => {
  const facade = finish.facade ?? "wood";
  if (facade === "wood") return prices.get(priceKeys.board(facadeGroup(project, module, finish)));
  const paint = facadePaintedOnBothSides({ facade, facadePaintSides: finish.facadePaintSides })
    ? 1 + prices.get(priceKeys.paintTwoSidedPercent) / 100
    : 1;
  // Фрезеровка — доплата только к МДФ в эмали; старый «глянец» уже с рисунком в цене.
  const pattern = finish.facadePattern ?? "plain";
  const milling = facadeSupportsMilling(facade) && facade !== "gloss" && pattern !== "plain"
    ? prices.get(priceKeys.pattern(pattern))
    : 0;
  return prices.get(priceKeys.facade(facade)) * paint + milling;
};

/** Ручка по прайсу: цена штуки и доплата за длину сверх стандартной. */
const handlePrice = (prices: PriceReader, config: HandleGeometry, doorWidthMm: number, doorHeightMm: number) => {
  const handle = normalizeHandleChoice(config.handle);
  const base = prices.get(priceKeys.handle(handle));
  const extra = handleExtraLengthMm({ ...config, handle }, doorWidthMm, doorHeightMm);
  return extra > 0
    ? base + extra * prices.get(priceKeys.handlePerMm(handle === "integrated" ? "integrated" : "bar"))
    : base;
};

const lightingPrice = (prices: PriceReader, quantities: LightingQuantities) =>
  (quantities.profileM ? quantities.profileM * prices.get(priceKeys.ledProfile) : 0) +
  (quantities.corners ? quantities.corners * prices.get(priceKeys.ledCorner) : 0) +
  (quantities.drivers ? quantities.drivers * prices.get(priceKeys.ledDriver) : 0);

/** Механизм откидного фасада: подъёмник Aventos по типу или механизм вниз, плюс петли, если нужны. */
const flapHardwarePrice = (prices: PriceReader, front: FlapFront, widthMm: number) =>
  prices.get(front.direction === "up" ? priceKeys.lift(front.lift!) : priceKeys.flapDown) +
  flapHingeCount(front, widthMm) * prices.get(priceKeys.hinge);

/**
 * Доплата за полки и ящики в своём декоре: площадь × (цена своей плиты или
 * фасада − цена плиты корпуса), не меньше нуля. Сами полка и ящик — в цене
 * строк «Полка» и «Ящик».
 */
const interiorDecorSurcharge = (prices: PriceReader, project: PlannerProject, module: WardrobeModule, carcassRate: number) => {
  let extra = 0;
  const shelfDecor = normalizeShelfDecor(module);
  if (shelfDecor && module.type !== "corner45") {
    const rate = prices.get(priceKeys.board(resolveDecor(shelfDecor).group));
    extra += Math.max(0, rate - carcassRate) * interiorShelfAreaM2(module);
  }
  const front = drawerFrontFinish(module);
  if (front.kind !== "carcass") {
    const rate = front.kind === "decor"
      ? prices.get(priceKeys.board(resolveDecor(front.code).group))
      : facadeRatePerM2(prices, project, module, { ...module, facadePattern: "plain" });
    extra += Math.max(0, rate - carcassRate) * interiorDrawerFrontsAreaM2(module);
  }
  return extra;
};

/**
 * Смета считает только то, что построено: плиты — по деталям из builtParts
 * (те же, что в 3D и в техкарте), фасады — по размерам створок и фронтов,
 * петли — по их числу на створках. Цены — только из прайса (кабинет →
 * «Цены»); незаданные попадают в missing.
 */
export const calculatePrice = (project: PlannerProject, priceValues: PriceValues | null | undefined): PriceBreakdown => {
  const prices = createPriceReader(priceValues);
  let carcass = 0;
  let internals = 0;
  let facades = 0;
  let hardware = 0;

  project.modules.forEach((module) => {
    const boardRate = () => prices.get(priceKeys.board(carcassGroup(project, module)));
    const parts = moduleBuiltPanels(project, module);
    const plainParts = parts.filter((part) => !part.decor && !part.painted);
    const boardArea = builtPanelsAreaM2(plainParts, "board");
    const backArea = builtPanelsAreaM2(parts, "back");
    if (boardArea > 0) carcass += boardArea * boardRate();
    // Столешница комода в своём декоре — по цене своей группы; цоколь в эмали —
    // по цене МДФ в эмали без фрезеровки (15.10).
    parts.forEach((part) => {
      if (part.kind !== "board") return;
      if (part.decor) carcass += builtPanelAreaM2(part) * prices.get(priceKeys.board(resolveDecor(part.decor).group));
      else if (part.painted) carcass += builtPanelAreaM2(part) * prices.get(priceKeys.facade("satin"));
    });
    // Задник может быть в своём декоре — и по цене своей группы.
    if (backArea > 0) carcass += backArea * prices.get(priceKeys.back(backPanelDecor(project, module).group));

    if (module.type === "end") {
      const baseType = cabinetBaseType(module);
      // Три опоры: открытые или за цокольными планками.
      if (baseType === "plinth" || baseType === "legs") hardware += 3 * prices.get(priceKeys.endLeg);
      return;
    }

    if (module.type === "shelving") {
      const grid = getShelvingGridLayout(module);
      if (module.shelvingBase === "legs") hardware += shelvingLegCount(module) * prices.get(priceKeys.leg);
      // Подсветка: профиль с лентой по длине и один блок питания на стеллаж.
      hardware += lightingPrice(prices, shelvingLightingQuantities(module));
      const drawerPaint = Boolean(chestDrawerPaintFinish(module));
      getShelvingCellLayouts(module, grid).forEach((layout) => {
        const cell: ShelvingCellConfig | undefined = layout.config;
        if (!cell) return;
        for (const board of getShelvingCellBoards(cell, layout.width, layout.height, module.depth)) {
          if (!board.glass) continue;
          // Стеклянная полка: стекло по своей цене и полкодержатели под стекло.
          internals += board.width * board.depth / 1_000_000 * prices.get(priceKeys.glassShelf) +
            prices.get(priceKeys.glassHolders);
        }
        if (cell.content !== "door" && cell.content !== "drawers") return;
        // Фасад — по настоящему размеру: накладной больше проёма, вкладной меньше.
        const rect = shelvingCellFacadeRect(module, layout, grid);
        const rate = facadeRatePerM2(prices, project, module, cell);
        // У ячеек ручка стандартной длины — без доплаты за длину.
        const handle = prices.get(priceKeys.handle(normalizeHandleChoice(cell.handle ?? "bar")));
        if (cell.content === "door") {
          // Одна или две распашные дверцы либо откидной фасад (09.10).
          const front = cellFrontLeaves(cell, rect.width, rect.height, shelvingCellHeadroomMm(project, module, cell.id, grid), shelvingExcludedLifts(module));
          front.leaves.forEach((leaf) => {
            facades += leaf.widthMm * leaf.heightMm * rate / 1_000_000;
            hardware += handle + (leaf.kind === "hinged" ? hingeCountForDoorHeight(leaf.heightMm) * prices.get(priceKeys.hinge) : 0);
          });
          if (front.flap) hardware += flapHardwarePrice(prices, front.flap, rect.width);
          return;
        }
        // Комод (16.10): над ящиками откидной вверх — свой фасад, подъёмник HK top и ручка.
        const split = chestCellSplit(module, cell, layout, grid);
        const flap = split ? flapFront("up", cell.doorLift, split.flapHeightMm, Infinity, CHEST_TOP_FLAP_EXCLUDED_LIFTS) : null;
        if (split && flap) {
          const flapRate = cell.chestTopFlapGlass ? facadeRatePerM2(prices, project, module, { facade: "glass" }) : rate;
          facades += split.flapRect.width * split.flapHeightMm * flapRate / 1_000_000;
          hardware += handle + flapHardwarePrice(prices, flap, split.flapRect.width);
        }
        const drawersHeight = split ? split.drawersRect.height : rect.height;
        const count = shelvingDrawerCount(cell.drawerCount, split ? split.drawersClearMm : layout.height, drawerLimits(module));
        // У комода со свесом нижний фасад выше остальных (15.10).
        const fronts = shelvingDrawerFrontHeights(module, layout, drawersHeight, count);
        facades += fronts.reduce((sum, height) => sum + rect.width * height, 0) * rate / 1_000_000;
        internals += count * prices.get(priceKeys.item("drawer"));
        // Комод: ящики покрашены внутри в цвет фасадов — доплата за ящик.
        if (drawerPaint) internals += count * prices.get(priceKeys.drawerPaint);
        hardware += count * handle;
      });
      return;
    }

    const corner = module.type === "corner45";
    if (corner) {
      // Плиты углового (корпус, полки, цоколь) — выше, из builtParts. На
      // ножках — по углам, у длинного корпуса ещё пара посередине.
      if (cabinetBaseType(module) === "legs" && cabinetBaseHeightMm(module) > 0) hardware += cornerLegPoints(module).length * prices.get(priceKeys.leg);
    } else if (module.type !== "mezzanine" && cabinetBaseType(module) === "legs") {
      hardware += 4 * prices.get(priceKeys.leg);
    }

    // Наружные ящики внизу: короба с направляющими и фронты в своей
    // отделке (под стеклянной или зеркальной дверью — ЛДСП). Полка над
    // ящиками — в деталях корпуса.
    const outerDrawers = module.type === "single" || module.type === "double" ? cabinetDrawerLayout(module) : null;
    if (outerDrawers) {
      const drawers = outerDrawers.count * outerDrawers.bays.length;
      const finish = cabinetDrawerFinish(module);
      internals += drawers * prices.get(priceKeys.item("drawer"));
      // Ручек нет — зацеп 20 мм над фасадом (31.10).
      if (!CABINET_DRAWERS_FINGER_PULL) hardware += drawers * prices.get(priceKeys.handle(normalizeHandleChoice(finish.handle)));
      facades += cabinetDrawerFrontsAreaM2(outerDrawers) *
        facadeRatePerM2(prices, project, module, { ...module, ...finish });
    }

    // Подсветка шкафа: ленты в полках или в стойках и блок питания.
    hardware += lightingPrice(prices, cabinetLightingQuantities(module));
    // Зеркало на открытой боковине — в строку «Фасады». Закрытое соседом
    // или торцевыми полками в заказ не идёт и не считается.
    const mirrors = activeSideMirrors(project, module);
    if (mirrors.length) {
      const mirror = sideMirrorSize(module);
      facades += mirrors.length * (mirror.width * mirror.height) / 1_000_000 * prices.get(priceKeys.sideMirror);
    }
    module.items.forEach((item) => {
      internals += prices.get(priceKeys.item(item.type)) * (corner ? 1 : internalItemPieceCount(module, item));
    });
    // Полки и лицевые панели ящиков в своём декоре (16.10): доплата — разница
    // цены плиты своей группы (или фасада) и корпуса за их площадь. У углового
    // полки — детали builtParts и уже по цене своего декора.
    internals += interiorDecorSurcharge(prices, project, module, boardRate());

    // Створки — как построены: ширина каждой с зазорами, высота над цоколем
    // или над ящиками минус 8 мм. Петли — по числу на створку, как в 3D.
    const leaves = cabinetDoorLeaves(project, module);
    if (leaves.length) {
      const rate = facadeRatePerM2(prices, project, module, module);
      // Створка со своим фасадом — по его цене (16.10).
      facades += leaves.reduce((sum, leaf, index) => sum + leaf.widthMm * leaf.heightMm *
        (doorFacadeOverride(module, index) ? facadeRatePerM2(prices, project, module, leafFinish(module, index)) : rate), 0) / 1_000_000;
      // Добор углового — в отделке дверей, без фрезеровки (у стекла и зеркала — ЛДСП).
      if (corner) {
        const fillerRate = facadeRatePerM2(prices, project, module, module.facade === "glass" || module.facade === "mirror"
          ? { facade: "wood" } : { ...module, facadePattern: "plain" });
        facades += leaves.reduce((sum, leaf) => sum + cornerFillerAreaM2(module, leaf.heightMm), 0) * fillerRate;
      }
      const handleHeight = Math.max(120, cabinetFacadeHeightMm(project, module));
      const flaps = mezzanineFlapLayout(module, moduleHeadroomMm(project, module));
      // У откидного фасада одна ручка, даже если он складной из двух частей.
      hardware += flaps
        ? flaps.reduce((sum, flap) => sum + handlePrice(prices, module, flap.widthMm, handleHeight), 0)
        : leaves.reduce((sum, leaf, index) => sum + handlePrice(prices,
          doorFacadeOverride(module, index) ? { ...module, handle: leafHandle(module, index) } : module, leaf.widthMm, handleHeight), 0);
      hardware += module.door === "sliding"
        ? prices.get(priceKeys.slidingSystem)
        : flaps
          ? flaps.reduce((sum, flap) => sum + flapHardwarePrice(prices, flap.front, flap.widthMm), 0)
          : leaves.reduce((sum, leaf) => sum + hingeCountForDoorHeight(leaf.heightMm), 0) * prices.get(priceKeys.hinge);
    }
  });

  const result = {
    carcass: roundHundreds(carcass),
    internals: roundHundreds(internals),
    facades: roundHundreds(facades),
    hardware: roundHundreds(hardware),
    total: 0,
    missing: [...prices.missing].map(priceLineTitle)
  };
  result.total = result.carcass + result.internals + result.facades + result.hardware;
  return result;
};

/** Итог можно показывать клиенту: все нужные цены заданы. */
export const priceIsComplete = (price: PriceBreakdown) => price.missing.length === 0;

export const formatPrice = (value: number) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value);
